import React, { useEffect, useRef } from 'react';
import {
    BaseStagePlugin,
    getFramework,
    useActiveOriginIndex,
    useIdentity,
    useScale,
    useViewHeight,
    useViewWidth,
} from '@/framework';
import { IExportPlugin, PluginType } from '@/version-types';
import { checkScaleKeydown } from '../hotkey-event/scale/utils';
import { CropImgPlugin } from './crop-img';
import CropImgWrap from './new-components/crop-img-wrap';
import cropImgPluginState, { useCropImgState } from './states/crop-img-state';

const CropImgView = React.memo(() => {
    const identity = useIdentity();
    const framework = getFramework(identity);
    const scale = useScale();
    const stageWidth = useViewWidth();
    const stageHeight = useViewHeight();
    const activeOriginIndex = useActiveOriginIndex();
    const isCropImage = useCropImgState()((state) => state.isCropImage);
    const isAutoCutoutLayer = useCropImgState()((state) => state.isAutoCutoutLayer);
    const nodeRef = useRef<HTMLDivElement>(null);
    const wheelY = useRef<number>(0);

    // 裁剪的时候也要可以滚轮缩放
    useEffect(() => {
        if (!isCropImage) return;
        const mouseWheelHandle = (ev) => {
            const isScaleKeyDown = ev.ctrlKey || checkScaleKeydown();
            if (!isScaleKeyDown) return;
            ev.preventDefault();
            wheelY.current += ev.deltaY;
            if (Math.abs(wheelY.current) >= 4) {
                if (ev.deltaY > 0) {
                    wheelY.current = 0;
                    framework.stage.zoomOut();
                } else if (ev.deltaY < 0) {
                    wheelY.current = 0;
                    framework.stage.zoomIn();
                }
            }
        };
        framework.getInstance(CropImgPlugin).setRootTarget(nodeRef.current);
        nodeRef.current?.addEventListener('wheel', mouseWheelHandle);
        return () => {
            nodeRef.current?.removeEventListener('wheel', mouseWheelHandle);
        };
    }, [isCropImage]);

    if (!isCropImage) return null;

    return (
        <div className="crop-img-plugin-wrap" ref={nodeRef}>
            <BaseStagePlugin originIndexOrLayerId={activeOriginIndex} name="crop-img-plugin">
                <CropImgWrap
                    scale={scale}
                    stageWidth={stageWidth}
                    stageHeight={stageHeight}
                    framework={framework}
                    identity={identity}
                    isAutoCutoutLayer={isAutoCutoutLayer}
                />
            </BaseStagePlugin>
        </div>
    );
});

const exportPlugin: IExportPlugin = {
    type: PluginType.STAGE,
    sort: 9,
    name: 'crop-img-plugin',
    render() {
        return <CropImgView />;
    },
    createInstance({ identity }) {
        return [{ key: 'CropImgPlugin', value: new CropImgPlugin(identity) }];
    },
    createState() {
        return [cropImgPluginState];
    },
    destroy({ getInstance }) {
        getInstance(CropImgPlugin).destroy();
    },
};

export default exportPlugin;
